import React, { useState } from 'react';
import { Check, MapPin, Star } from 'lucide-react';
import { forfaitsEquin, forfaitsCanin } from '../mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';

const Pricing = () => {
  const [activeTab, setActiveTab] = useState('equin');

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#8B9E8A] mb-4">
            Tarifs & Forfaits
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des formules adaptées à tous les besoins, avec des forfaits avantageux pour un suivi régulier
          </p>
        </div>

        {/* Pricing Tabs */}
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-12 bg-[#8B9E8A]/10 max-w-md mx-auto">
              <TabsTrigger 
                value="equin"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8B9E8A] data-[state=active]:to-[#9CAF88] data-[state=active]:text-white font-semibold text-lg py-3"
              >
                🐴 Équin
              </TabsTrigger>
              <TabsTrigger 
                value="canin"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8B9E8A] data-[state=active]:to-[#9CAF88] data-[state=active]:text-white font-semibold text-lg py-3"
              >
                🐕 Canin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="equin" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {forfaitsEquin.map((forfait, index) => (
                  <Card 
                    key={index} 
                    className={`relative border-2 hover:shadow-2xl transition-all duration-300 hover:scale-105 ${
                      forfait.popular 
                        ? 'border-[#C5A572] bg-gradient-to-br from-[#C5A572]/5 to-white' 
                        : 'border-[#8B9E8A]/20 bg-white'
                    }`}
                  >
                    {forfait.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white px-4 py-1 text-sm font-bold">
                          <Star size={14} className="mr-1" />
                          LE PLUS POPULAIRE
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-2xl font-bold text-[#8B9E8A] mb-2">
                        {forfait.name}
                      </CardTitle>
                      <div className="text-4xl font-bold text-[#C5A572] mb-2">
                        {forfait.price}
                      </div>
                      <CardDescription className="text-green-600 font-semibold">
                        {forfait.savings}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-start gap-2">
                          <Check className="text-[#C5A572] mt-1 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{forfait.sessions}</span>
                        </div>
                        <p className="text-gray-600 text-sm">{forfait.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="canin" className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {forfaitsCanin.map((forfait, index) => (
                  <Card 
                    key={index} 
                    className={`relative border-2 hover:shadow-2xl transition-all duration-300 hover:scale-105 ${
                      forfait.popular 
                        ? 'border-[#C5A572] bg-gradient-to-br from-[#C5A572]/5 to-white' 
                        : 'border-[#8B9E8A]/20 bg-white'
                    }`}
                  >
                    {forfait.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-gradient-to-r from-[#C5A572] to-[#B8860B] text-white px-4 py-1 text-sm font-bold">
                          <Star size={14} className="mr-1" />
                          LE PLUS POPULAIRE
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-2xl font-bold text-[#8B9E8A] mb-2">
                        {forfait.name}
                      </CardTitle>
                      <div className="text-4xl font-bold text-[#C5A572] mb-2">
                        {forfait.price}
                      </div>
                      <CardDescription className="text-green-600 font-semibold">
                        {forfait.savings}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-start gap-2">
                          <Check className="text-[#C5A572] mt-1 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{forfait.sessions}</span>
                        </div>
                        <p className="text-gray-600 text-sm">{forfait.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* CTA vers Zone d'intervention */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            Consultez notre zone d'intervention et les frais de déplacement
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('zone-intervention');
              if (element) {
                const offset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }}
            className="bg-[#8B9E8A]/10 text-[#8B9E8A] border-2 border-[#8B9E8A]/30 px-8 py-3 rounded-full font-bold hover:bg-[#8B9E8A]/20 transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
          >
            <MapPin size={20} />
            Voir la zone d'intervention
          </button>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
